import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ImportedAccount {
  id: string;
  profile_id: string | null;
  email: string;
  full_name: string | null;
  account_type: string;
  claim_status: string;
  imported_at: string;
  last_email_sent_at: string | null;
  claimed_at: string | null;
  imported_by: string | null;
  setup_completed?: boolean;
  // Talent-specific fields
  talent_type?: string;
  sport?: string;
  team_name?: string;
  phone?: string;
  athlete_level?: string;
  instagram?: string;
  tiktok?: string;
  youtube?: string;
  x_handle?: string;
  threads?: string;
  music_genre?: string;
  label_status?: string;
  label_name?: string;
  entertainer_genres?: string;
}

interface Filters {
  status: 'all' | 'pending' | 'claimed';
  type: 'all' | 'talent' | 'brand';
  search: string;
}

export const useImportedAccounts = () => {
  const { toast } = useToast();
  const [accounts, setAccounts] = useState<ImportedAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<Filters>({ status: 'all', type: 'all', search: '' });

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch account_imports - we'll use any type since this table is new
      const { data, error } = await supabase
        .from('account_imports' as any)
        .select('*')
        .order('imported_at', { ascending: false });

      if (error) throw error;

      // Now fetch profiles to check setup_completed for each
      const profileIds = (data || []).filter((d: any) => d.profile_id).map((d: any) => d.profile_id);
      let profileMap: Record<string, boolean> = {};
      let talentMap: Record<string, Partial<ImportedAccount>> = {};
      
      if (profileIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, setup_completed')
          .in('id', profileIds);
        
        if (profiles) {
          profiles.forEach((p: any) => {
            profileMap[p.id] = p.setup_completed === true;
          });
        }

        // Fetch talent data for enrichment
        const { data: talents } = await supabase
          .from('talents')
          .select('profile_id, talent_type, sport, team_name, cell_phone, social_profiles, stats, competition_level')
          .in('profile_id', profileIds);

        if (talents) {
          talents.forEach((t: any) => {
            const socials = t.social_profiles as Record<string, any> | null;
            const statsData = t.stats as Record<string, any> | null;
            talentMap[t.profile_id] = {
              talent_type: t.talent_type || undefined,
              sport: t.sport || undefined,
              team_name: t.team_name || undefined,
              phone: t.cell_phone || undefined,
              athlete_level: t.competition_level || undefined,
              instagram: socials?.instagram || undefined,
              tiktok: socials?.tiktok || undefined,
              youtube: socials?.youtube || undefined,
              x_handle: socials?.x_handle || socials?.twitter || undefined,
              threads: socials?.threads || undefined,
              music_genre: statsData?.music_genre || undefined,
              label_status: statsData?.label_status || undefined,
              label_name: statsData?.label_name || undefined,
              entertainer_genres: statsData?.entertainer_genres || undefined,
            };
          });
        }
      }

      const enriched: ImportedAccount[] = (data || []).map((row: any) => ({
        ...row,
        setup_completed: row.profile_id ? profileMap[row.profile_id] || false : false,
        ...(row.profile_id && talentMap[row.profile_id] ? talentMap[row.profile_id] : {}),
      }));

      setAccounts(enriched);
    } catch (error: any) {
      console.error('Error fetching imported accounts:', error);
      toast({ title: 'Error', description: 'Failed to load imported accounts', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const filteredAccounts = accounts.filter(a => {
    if (filters.status !== 'all' && a.claim_status !== filters.status) return false;
    if (filters.type !== 'all' && a.account_type !== filters.type) return false;
    if (filters.search) {
      const s = filters.search.toLowerCase();
      if (!a.email.toLowerCase().includes(s) && !(a.full_name || '').toLowerCase().includes(s)) return false;
    }
    return true;
  });

  const resendEmail = async (importId: string) => {
    try {
      const { error } = await supabase.functions.invoke('resend-claim-email', {
        body: { import_id: importId },
      });
      if (error) throw error;
      toast({ title: 'Email sent', description: 'Claim email has been resent.' });
      fetchAccounts();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to resend email', variant: 'destructive' });
    }
  };

  const resendAllPending = async () => {
    const pending = accounts.filter(a => a.claim_status === 'pending');
    let sent = 0;
    for (const account of pending) {
      try {
        await supabase.functions.invoke('resend-claim-email', {
          body: { import_id: account.id },
        });
        sent++;
      } catch { /* continue */ }
    }
    toast({ title: 'Bulk resend complete', description: `Sent ${sent} of ${pending.length} emails.` });
    fetchAccounts();
  };

  const deleteImport = async (importId: string) => {
    try {
      const { error } = await supabase
        .from('account_imports' as any)
        .delete()
        .eq('id', importId);
      if (error) throw error;
      toast({ title: 'Deleted', description: 'Import record removed.' });
      setAccounts(prev => prev.filter(a => a.id !== importId));
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const stats = {
    total: accounts.length,
    claimed: accounts.filter(a => a.claim_status === 'claimed').length,
    pending: accounts.filter(a => a.claim_status === 'pending').length,
    emailsSent: accounts.filter(a => a.last_email_sent_at).length,
  };

  return {
    accounts: filteredAccounts,
    loading,
    filters,
    setFilters,
    stats,
    resendEmail,
    resendAllPending,
    deleteImport,
    refetch: fetchAccounts,
  };
};
